/**
 * HorizontalViewer Component
 * Renders webtoon images in a horizontal scroll/snap layout (Comic Mode).
 * 
 * @param {HTMLElement} container - Container to render into.
 * @param {Object} props
 * @param {Array} props.images - Array of image URLs.
 * @param {Function} props.onNext - Callback for next episode (when trying to go past last page).
 * @param {Function} props.onPrev - Callback for prev episode (when trying to go before first page).
 * @param {Function} props.onToggleUI - Callback to toggle header/footer visibility.
 */
export const HorizontalViewer = (container, { images, onNext, onPrev, onToggleUI, onHideUI, isEmbedded }) => {
    // 1. Inject Styles
    // 1. Inject Styles (Moved to public/viewer.css)
    // Style injection block removed.

    // 2. Render Template
    const imageListHtml = images.map((url, index) => {
        let finalUrl = url;
        if (url.includes('pstatic.net') || url.includes('naver.com') || url.includes('daumcdn.net')) {
            finalUrl = "https://wsrv.nl/?url=" + encodeURIComponent(url) + "&w=1200&output=webp";
        }

        return `
            <div class="comic-slide" style="flex: 0 0 100vw; width: 100vw; height: 100vh; display: flex; align-items: center; justify-content: center; scroll-snap-align: center;">
                <div class="comic-image-wrapper webtoon-image-wrapper" style="position: relative; width: fit-content; height: fit-content; max-width: 720px; margin: auto;">
                    <img src="${finalUrl}" 
                         class="comic-image" 
                         alt="Page ${index + 1}" 
                         style="max-width: 100%; max-height: 100vh; width: auto; height: auto; display: block;"
                         loading="${index < 5 ? 'eager' : 'lazy'}"
                         oncontextmenu="return false;"
                         onerror="if(!this.src.includes('wsrv.nl')) this.src='https://wsrv.nl/?url='+encodeURIComponent('${url}')"
                    />
                </div>
            </div>
        `;
    }).join('');

    container.innerHTML = `
        <!-- [FIX] Explicit Styles for Horizontal Scroll & Snap -->
        <div id="comic-scroll-container" class="comic-mode-container ${isEmbedded ? 'embedded' : ''}"
             style="display: flex; flex-direction: row; overflow-x: auto; scroll-snap-type: x mandatory; width: 100vw; height: 100vh; overscroll-behavior-x: contain;">
            ${images.length > 0 ? imageListHtml : "<div class='text-white m-auto'>No Images</div>"}
        </div>
        <div id="page-indicator" class="comic-page-indicator">1 / ${images.length}</div>
        
        <!-- Next/Prev Buttons overlay or end of slide card? 
             For "Manga" style, usually the last page swipe triggers next. 
             But keeping it simple based on previous logic.
             The previous logic was swipe-based or tap zones.
        -->
    `;
    const indicator = container.querySelector('#page-indicator');
    const scrollContainer = container.querySelector('#comic-scroll-container');

    // Update Indicator
    scrollContainer.onscroll = () => {
        const scrolled = scrollContainer.scrollLeft;
        const width = scrollContainer.clientWidth;
        const pageIndex = Math.round(scrolled / width);

        // Check boundaries for "Pull to Next/Prev" could go here, but standard overflow-x handles it mostly visually.
        // We update text.
        indicator.innerText = `${Math.min(pageIndex + 1, images.length)} / ${images.length}`;
    };

    // Tap Zones
    scrollContainer.onclick = (e) => {
        const width = scrollContainer.clientWidth;
        const rect = scrollContainer.getBoundingClientRect();
        const x = e.clientX - rect.left; // Relative X coordinate

        // Center (30% - 70%) -> Toggle UI
        if (x > width * 0.3 && x < width * 0.7) {
            if (onToggleUI) onToggleUI();

            // Toggle indicator visibility too
            indicator.style.opacity = indicator.style.opacity === '0' ? '1' : '0';
        }
        // Left (0% - 30%) -> Prev Page
        else if (x <= width * 0.3) {
            if (scrollContainer.scrollLeft <= 0) {
                // At start, call prev episode
                if (onPrev) onPrev();
            } else {
                scrollContainer.scrollBy({ left: -width, behavior: 'smooth' });
                // Hide UI for immersion
                if (onHideUI) onHideUI();
                indicator.style.opacity = '0';
            }
        }
        // Right (70% - 100%) -> Next Page
        else {
            if (scrollContainer.scrollLeft + width >= scrollContainer.scrollWidth - 10) { // Tolerance
                // At end, call next episode
                if (onNext) onNext();
            } else {
                scrollContainer.scrollBy({ left: width, behavior: 'smooth' });
                // Hide UI for immersion
                if (onHideUI) onHideUI();
                indicator.style.opacity = '0';
            }
        }
    };
    // 4. Return Interface
    return {
        nextPage: () => {
            const width = scrollContainer.clientWidth;
            if (scrollContainer.scrollLeft + width >= scrollContainer.scrollWidth - 10) {
                if (onNext) onNext();
            } else {
                scrollContainer.scrollBy({ left: width, behavior: 'smooth' });
            }
        },
        prevPage: () => {
            const width = scrollContainer.clientWidth;
            if (scrollContainer.scrollLeft <= 0) {
                if (onPrev) onPrev();
            } else {
                scrollContainer.scrollBy({ left: -width, behavior: 'smooth' });
            }
        }
    };
};
